import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PackagePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { fmtDate, num, rupiah } from "@/lib/format";

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export function AdditionHistory({ additions }) {
  if (!additions?.length) return null;
  return (
    <div className="border border-sky-200 bg-sky-50/60 rounded-sm p-3 space-y-1 text-xs" data-testid="addition-history">
      <div className="font-semibold text-sky-900">Riwayat Tambahan Voucher</div>
      {additions.map((a) => (
        <div key={a.rev} className="flex flex-wrap gap-x-3" data-testid={`addition-row-${a.rev}`}>
          <span className="font-mono">R{a.rev}</span><span>{fmtDate(a.date)}</span>
          <span>{a.lines.map((l) => `${l.voucher_name} +${num(l.qty)}`).join(", ")}</span>
          <span className="font-mono font-semibold">{rupiah(a.total)}</span><span className="text-slate-500">oleh {a.by}{a.note ? ` · ${a.note}` : ""}</span>
        </div>
      ))}
    </div>
  );
}

export function AdditionPanel({ inv, onChanged }) {
  const [types, setTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState({});
  const [date, setDate] = useState(today());
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open && !types.length) api.get("/prices").then((r) => setTypes(r.data.filter((v) => v.active))).catch((e) => toast.error(errMsg(e)));
  }, [open, types.length]);
  const priceOf = (v) => inv.lines.find((l) => l.voucher_type_id === v.id)?.price ?? v.price;
  const total = types.reduce((s, v) => s + (qty[v.id] || 0) * priceOf(v), 0);
  const submit = async () => {
    setBusy(true);
    try {
      const lines = Object.entries(qty).filter(([, q]) => q > 0).map(([voucher_type_id, q]) => ({ voucher_type_id, qty: q }));
      await api.post(`/invoices/${inv.id}/additions`, { lines, date, note });
      toast.success("Tambahan voucher tersimpan. Nota versi baru siap dicetak ulang.");
      setOpen(false); setQty({}); setNote("");
      onChanged();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!open) return <Button variant="outline" onClick={() => setOpen(true)} disabled={!!inv.revision_state} className="w-full sm:w-auto border-sky-300 text-sky-800" data-testid="addition-open-btn"><PackagePlus className="h-4 w-4 mr-2" />Tambah Voucher</Button>;
  return (
    <div className="border border-sky-300 bg-sky-50/60 rounded-sm p-3 space-y-3" data-testid="addition-panel">
      <div className="text-sm font-semibold text-sky-900">Tambah Voucher (sebelum penarikan)</div>
      <p className="text-xs text-slate-600">Jumlah ditambahkan ke invoice, tagihan & hutang pelanggan ikut bertambah. Nota menjadi versi R{(inv.revision || 0) + 1} dan nota lama ditandai DIGANTI.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {types.map((v, i) => (
          <label key={v.id} className="text-xs space-y-1">
            <span className="block">{v.name} <span className="font-mono text-slate-500">@ {num(priceOf(v))}</span></span>
            <Input type="number" min={0} value={qty[v.id] || ""} placeholder="0" className="font-mono" onChange={(e) => setQty((s) => ({ ...s, [v.id]: Math.max(0, parseInt(e.target.value, 10) || 0) }))} data-testid={`addition-qty-${i}`} />
          </label>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="addition-date-input" />
        <Input placeholder="Catatan (opsional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="addition-note-input" />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm">Nilai tambahan: <b className="font-mono" data-testid="addition-total">{rupiah(total)}</b></span>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="addition-cancel-btn">Batal</Button>
          <Button disabled={busy || total <= 0} onClick={submit} className="bg-sky-700 hover:bg-sky-800" data-testid="addition-submit-btn">Simpan Tambahan</Button>
        </div>
      </div>
    </div>
  );
}
